import React from 'react';
import PropTypes from 'prop-types';
import he from 'he';
import SortArrow from './SortArrow';
import {trackUserClick} from '@forwardfinancing/analytics-frontend-library';

class TableBody extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      sortColumn: null,
      sortOrder: null
    };

    this.buildRows = this.buildRows.bind(this);
    this.buildHeaders = this.buildHeaders.bind(this);
    this.buildCells = this.buildCells.bind(this);
    this.errorMessage = this.errorMessage.bind(this);
    this.noDataMessage = this.noDataMessage.bind(this);
    this.firstLoad = true;
  }

  sortQuery(header) {
    let lastSortOrder = this.state.sortOrder;
    if (header !== this.state.sortColumn) {
      lastSortOrder = null;
    }
    // is desc when the stats's sortOrder is null
    let sortOrder = lastSortOrder === 'desc' ? 'asc' : 'desc';

    this.props.sortQuery &&
      this.setState(
        {sortColumn: header, sortOrder},
        () => this.props.sortQuery(
          this.state.sortColumn,
          this.state.sortOrder
        )
      );
  }

  getMatchingDataLabelForGivenValue(value) {
    const matches = this.props.dataLabels.map((header) => {
      if (Object.keys(header).includes(value)) {
        return `${header[value]}`;
      }
      return false;
    });
    // remove undefined values and return first match
    return matches.filter(Boolean)[0];
  }

  renderOrderArrowIcon(column){
    let selected = this.state.sortColumn === column;
    let direction = this.state.sortOrder;

    return(
      <SortArrow
        topFill={direction === 'asc'}
        bottomFill={direction === 'desc'}
        selected={selected}
      />
  );
  }

  renderSortArrow(name, onClickFunc, sortValue){
    return (
      <div
        className="table-body__order"
        name={name}
      >
        {sortValue && this.renderOrderArrowIcon(sortValue)}
      </div>
    );
  }

  buildHeaders(headers, shouldUseSortArrows){
    return(
      headers.map((header, index) => {
        let key = Object.keys(header)[0];
        let sortValue = Object.keys(header).map(key => {
          return header[key];
        })[0];
        return(
          <th
            className={this.buildHeaderClassString(header.classes)}
            key={index}
            name={key}
            value={sortValue}
            onClick={() => this.sortQuery(sortValue)}
          >
          <div className="table-body__header-inner-wrap">
            <div className="table-body__header-value">
              {key}
            </div>
            {
              shouldUseSortArrows &&
              this.renderSortArrow(
                this.state.sortOrder,
                this.state.sortOrder,
                sortValue
              )
            }
            </div>
          </th>
        );
      })
    );
  }

  buildHeaderClassString(additionalClasses) {
    let classesString = 'table-body__header';

    if (additionalClasses) {
      classesString += ' ' + additionalClasses;
    }

    return classesString;
  }

  determineOnClick(key, info) {
    // key is only needed for "Select Terms" case to differentiate between
    // "Select Terms" and "Apply for Renewal"
    if (key === 'calculator_url' && !['Apply for Renewal', 'Submit Stipulations'].includes(info.text)){
      return trackUserClick({attribute: 'Select Terms'});
    } else if (info.text === 'Apply for Renewal') {
      return trackUserClick({attribute: `Apply for Renewal - ${info.source}`});
    } else if (info.text === 'Submit Stipulations') {
      return trackUserClick({attribute: "Submit Stipulations"});
    } else {
      return null;
    }
  }

  buildCreatedCell(key, data) {
    const htmlKeys = [
      'calculator_url', 'renewal_eligibility'
    ];
    const info = this.props.renderActionCell(data);
    if (this.props.isAdmin && info){
      info.href = '#';
    }

    if (htmlKeys.includes(key) && info === null) {
      // no action cell needed
      return null;
    } else if (htmlKeys.includes(key) && info.href !== null) {
      // action cell with link needed
      return (
        <span
          className="markup-cell"
          onClick={() => this.determineOnClick(key, info)}
          data={this.getMatchingDataLabelForGivenValue(key)}
          info={info.text}
        >
          <a
            href={info.href}
          >
            {info.text}
          </a>
        </span>
      );
    } else if (htmlKeys.includes(key) && info.href === null) {
      // action cell with out link needed
      return (
        <span
          className="markup-cell"
          onClick={() => this.determineOnClick(key, info)}
          data={this.getMatchingDataLabelForGivenValue(key)}
          info={info.text}
        >
          {info.text}
        </span>
      );
    } else {
      // sanitized cell info --> decoded
      // this is to ensure a char such as & is displayed correctly
      return (
        <span
          className="decoded-cell"
          data={this.getMatchingDataLabelForGivenValue(key)}
        >
          {he.decode(data[key])}
        </span>
      );
    }
  }

  buildCells(rowData){
    const keys = this.props.values.map((key)=> {
      return Object.keys(key)[0];
    });

    const specialKeys = [
      'calculator_url',
      'renewal_eligibility',
      'partner_action?',
      'dba',
      'legal_name'
    ];

    return(
      keys.map((key, index) => {
        // only use this when we need to, which includes names only right now
        // we use dangerouslySetInnerHTML here because this information is coming from
        // our endpoint, and can be trusted. In a case where we may be rendering
        // user input directly - we would not use dangerouslySetInnerHTML
        // due to  XSS script risks
        const isSpecial = specialKeys.includes(key);
        return(
          <td key={index}>
            {
              !isSpecial ?
              <span
                className="cell"
                data={this.getMatchingDataLabelForGivenValue(key)}
              >
                {rowData[key]}
              </span> :
              this.buildCreatedCell(key, rowData)
            }
          </td>
        );
      })
    );
  }

  buildRows(tableData){
    if (tableData.length > 0) return(tableData.map((row, index) => {
        return(
          <tr key={index}>
            {this.buildCells(row)}
          </tr>
        );
      })
    );
  }

  errorMessage(){
    return(
      <div className='table-error-msg'>
        <hr/>
        <p className="unavailable-message">
          This data is temporarily unavailable. Please try again or contact your Account Manager with questions.
        </p>
      </div>
    );
  }

  noDataMessage(){
    if (this.firstLoad && this.props.hasSpinner) {
      return(
        <div className="spinner"/>
      );
    } else {
      return(
        <div className="table-error-msg">
          <hr/>
          <p className="no-data-message">
            {this.props.noDataMessage}
          </p>
        </div>
      );
    }
  }

  render(){
    if(this.props.searchError){
      return this.errorMessage();
    } else if (this.props.tableData.length === 0) {
      return this.noDataMessage();
    } else {
      //note: The flaw with this strategy is that if the database is empty,
      //the pipeline will only show the spinning wheel
      this.firstLoad = false;
      return(
        <table className="table-body table is-striped is-narrow">
          <thead>
            <tr>
              {this.buildHeaders(this.props.headers, this.props.useSortArrows)}
            </tr>
          </thead>
          <tbody>
            {this.buildRows(this.props.tableData)}
          </tbody>
        </table>
      );
    }
  }
}

TableBody.propTypes = {
  values: PropTypes.array.isRequired,
  headers: PropTypes.array.isRequired,
  dataLabels: PropTypes.array.isRequired,
  tableData: PropTypes.array,
  sortQuery: PropTypes.func,
  searchError: PropTypes.bool,
  renderActionCell: PropTypes.func,
  useSortArrows: PropTypes.bool,
  isAdmin: PropTypes.bool,
  noDataMessage: PropTypes.string,
  hasSpinner: PropTypes.bool
};

TableBody.defaultProps = {
  useSortArrows: true,
  noDataMessage: 'No information present, please try another search.',
  hasSpinner: false
};

export default TableBody;
